import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import { applyStyles } from "./applyStyles";
import { BASE_PIN_OFFSET, BASE_Z_INDEX } from "./StickyWrapper.styled";
import { getSmartOffset, getStickerState } from "./stickerUtilities";
export const StickyContext = React.createContext({
    update: () => { },
    debouncedUpdate: debounce(() => { }, 300),
    stickersRef: { current: [] },
    verticalScrollParentRef: undefined
});
export const StickyProvider = (props) => {
    const { children, verticalScrollParentRef, stickyOffsetTop = 0 } = props;
    const stickersRef = useRef([]);
    const update = useCallback(() => {
        const stickers = stickersRef.current;
        if (stickers.length === 0) {
            return;
        }
        // 1) Update all rects, scroll bars, detect if sticker could still be on screen.
        stickers.forEach((sticker) => {
            Object.assign(sticker, getStickerState(sticker));
        });
        // 2) sort by top
        stickers.sort((first, second) => {
            if (first.ghostRect.top < second.ghostRect.top) {
                return -1;
            }
            if (first.ghostRect.top > second.ghostRect.top) {
                return 1;
            }
            return 0;
        });
        // 3) Apply sticky and stickyOffsetTop
        let zIndexOffset = stickers.filter((s) => !s.isDisabled).length + BASE_Z_INDEX - 1;
        stickers.forEach((sticker, i) => {
            // TODO optimize for not on-screen stickers?
            const previousStickers = stickers.slice(0, i);
            const smartOffsetTop = stickyOffsetTop + getSmartOffset(previousStickers, sticker);
            sticker.isSticky = sticker.ghostRect.top < smartOffsetTop;
            sticker.stickyOffsetTop = smartOffsetTop;
            if (!sticker.isDisabled) {
                sticker.zIndexOffset = zIndexOffset;
                zIndexOffset--;
            }
        });
        /**
         * 4) Update stickyOffsetBottom (combined height of stickers after the current one)
         * 5) Apply isPinned (calculated top position is below a "smart bottom offset")
        position parent bottom coordinate - combined height of other stickers + "base pin offset" = 24px)
    
         * 6) Apply styles
         */
        stickers.forEach((sticker, i) => {
            // depends on isSticky being calculated for all items prior to this
            const nextStickers = stickers.slice(i + 1, stickers.length).filter((s) => s.isSticky);
            sticker.stickyOffsetBottom = getSmartOffset(nextStickers, sticker);
            sticker.isPinned =
                sticker.positionParentRect.bottom - sticker.stickyOffsetBottom - BASE_PIN_OFFSET <
                    sticker.stickyOffsetTop + sticker.ghostRect.height - sticker.horizontalScrollBarHeight;
            sticker.wrapper.sticker = sticker;
            applyStyles(sticker);
        });
    }, [stickyOffsetTop]);
    const debouncedUpdate = useMemo(() => debounce(update, 16), [update]);
    useEffect(() => {
        // DES-3344 - Ensures measurements are updated after transitioned transforms that could throw off
        // of getBoundingClientRect measurements (like when a Dialog is animating)
        const updateAfterTransitionedTransforms = ({ target, propertyName }) => {
            const wasTransformed = propertyName === "transform" &&
                target instanceof Element &&
                stickersRef.current.some(({ positionParent: parent }) => target.contains(parent));
            if (wasTransformed) {
                update();
            }
        };
        window.addEventListener("transitionend", updateAfterTransitionedTransforms);
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        update();
        return () => {
            window.removeEventListener("transitionend", updateAfterTransitionedTransforms);
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [update]);
    const contextValue = useMemo(() => ({ update, debouncedUpdate, stickersRef, verticalScrollParentRef }), [update, debouncedUpdate, verticalScrollParentRef]);
    return _jsx(StickyContext.Provider, { value: contextValue, children: children });
};
